/* eslint-disable react/no-did-update-set-state */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

const breakpointProps = PropTypes.shape({
    xs: PropTypes.number,
    sm: PropTypes.number,
    md: PropTypes.number,
    lg: PropTypes.number,
    xl: PropTypes.number
});

const propTypes = {
    /**
     * Number of columns to occupy in the grid, or for a given breakpoint
     * Range: 1...12
     * @var {number|Object} width Either a number or an object of numbers per breakpoint
     * @default 12
     */
    width: PropTypes.oneOfType([PropTypes.number, breakpointProps]),
    /**
     * Number of columns to push to the left or right. Other Cells may be pushed or "pulled" as a result.
     * Range -12...-1, 1...12
     * @var {number|Object} push Either a number or an object of numbers per breakpoint
     * @default null
     */
    push: PropTypes.oneOfType([PropTypes.number, breakpointProps]),
    /**
     * Number of columns to shift to the left or right. Shifted Cells are relatively positioned.
     * Range -12...-1, 1...12
     * @var {number|Object} shift Either a number or an object of numbers per breakpoint
     * @default null
     */
    shift: PropTypes.oneOfType([PropTypes.number, breakpointProps]),
    /**
     * Class or space separated list of classes to apply
     * @var {string} className
     * @default ''
     */
    className: PropTypes.string,
    /**
     * DOM nodes to wrap within this Cell
     * @var {DOMNode} children
     * @default null
     */
    children: PropTypes.node
};

const defaultProps = {
    width: 12,
    push: null,
    shift: null,
    className: ''
};

const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];

/**
 * Will output a div that uses the grid and breakpoint system defined in Sarsaparilla
 *
 * Classes set on the Cell will be cached and rebuilt when width, push, shift or the className changes
 *
 * @see {@link http://localhost:3001/commonLayout#Cell|Local StyleGuide Cell Examples}
 * @author Michael Stack <stack_michael@bah.com>
 * @class
 */
export default class Cell extends PureComponent {
    static buildClasses({ width, push, shift, className }) {
        const classes = ['rec-grid-col'];

        // Include any custom classes; one size doesn't fit all
        if (className !== '') {
            classes.push(className);
        }

        // Set the width of the Cell
        let setWidth = false;
        if (typeof width === 'object') {
            // Set a width for each breakpoint
            breakpoints.forEach((breakpoint) => {
                const bWidth = width[breakpoint];
                if (typeof bWidth === 'number' && bWidth >= 1 && bWidth <= 12) {
                    classes.push(`rec-grid-${breakpoint}-${bWidth}-12`);
                    setWidth = true;
                }
            });
        }
        // Set either the xs width or a default of 12
        if (typeof width === 'number' || !setWidth) {
            if (width >= 1 && width <= 12) {
                classes.push(`rec-grid-xs-${width}-12`);
            }
            else {
                // Use 12 when given 0 or bad breakpoints
                classes.push('rec-grid-xs-12-12');
            }
        }

        if (push !== null) {
            if (typeof push === 'number') {
                // Set xs push class
                if (push >= 1 && push <= 12) {
                    // Cell will be pushed to the right
                    classes.push(`rec-grid-push-xs-${push}-12`);
                }
                else if (push <= -1 && push >= -12) {
                    // Cell will be pulled to the left
                    classes.push(`rec-grid-pull-xs-${-1 * push}-12`);
                }
            }
            else {
                // Set push values per breakpoint
                breakpoints.forEach((breakpoint) => {
                    const bPush = push[breakpoint];
                    if (typeof bPush === 'number') {
                        if (bPush >= 1 && bPush <= 12) {
                            // Cell will be pushed to the right
                            classes.push(`rec-grid-push-${breakpoint}-${bPush}-12`);
                        }
                        else if (bPush <= -1 && bPush >= -12) {
                            // Cell will be pulled to the left
                            classes.push(`rec-grid-pull-${breakpoint}-${-1 * bPush}-12`);
                        }
                    }
                });
            }
        }

        if (shift !== null) {
            if (typeof shift === 'number') {
                // Set xs shift class
                if (shift >= 1 && shift <= 12) {
                    // Cell will be shifted to the right
                    classes.push(`rec-grid-shift-xs-${shift}-12`);
                }
                else if (shift <= -1 && shift >= -12) {
                    // Cell will be unshifted to the left
                    classes.push(`rec-grid-unshift-xs-${-1 * shift}-12`);
                }
            }
            else {
                // Set shift values per breakpoint
                breakpoints.forEach((breakpoint) => {
                    const bShift = shift[breakpoint];
                    if (typeof bShift === 'number') {
                        if (bShift >= 1 && bShift <= 12) {
                            // Cell will be shifted to the right
                            classes.push(`rec-grid-shift-${breakpoint}-${bShift}-12`);
                        }
                        else if (bShift <= -1 && bShift >= -12) {
                            // Cell will be unshifted to the left
                            classes.push(`rec-grid-unshift-${breakpoint}-${-1 * bShift}-12`);
                        }
                    }
                });
            }
        }

        return classes.join(' ');
    }

    constructor(props) {
        super(props);

        this.state = {
            classes: Cell.buildClasses(this.props)
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.width !== this.props.width
            || prevProps.push !== this.props.push
            || prevProps.shift !== this.props.shift
            || prevProps.className !== this.props.className) {
            this.setState((prevState) => ({
                ...prevState,
                classes: Cell.buildClasses(this.props)
            }));
        }
    }

    render() {
        const { children, ...rest } = this.props;
        const { classes } = this.state;

        delete rest.className;
        delete rest.width;
        delete rest.push;
        delete rest.shift;

        return (
            <div className={classes} {...rest}>
                {children}
            </div>
        );
    }
}

Cell.propTypes = propTypes;
Cell.defaultProps = defaultProps;
